import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import type { TimerSession } from '@dt-pm/core';
import { useToast } from '@/design-system';
import { api } from '@/shared/lib/api';
import { formatApiError } from './api-error';

/**
 * Controle de tempo global. O BFF é a fonte da verdade: a identidade logada tem no máximo uma
 * sessão RUNNING (`POST /projects/:id/timer`, `POST /timer/:timerSessionId/stop`,
 * `GET /timer/current`). O estado é compartilhado entre o header, o quadro e o modal.
 */
interface TaskTimerValue {
  /** Task dona da sessão em andamento. */
  runningTaskId: string | null;
  runningProjectId: string | null;
  runningProjectName: string | null;
  runningTaskName: string | null;
  miniPlayerVisible: boolean;
  setMiniPlayerVisible: (visible: boolean) => void;
  toggleMiniPlayerVisible: () => void;
  /** Segundos a exibir: apenas o decorrido da sessão ativa retornada pelo current. */
  secondsFor: (taskId: string) => number;
  /** Inicia o timer do item (encerrando o anterior) ou encerra o que estiver rodando nele. */
  toggle: (
    taskId: string,
    projectId: string,
    metadata?: { projectName?: string | null; taskName?: string | null },
  ) => Promise<void>;
  /** Requisição em andamento — trava o botão para não duplicar o start. */
  busy: boolean;
}

const TaskTimerContext = createContext<TaskTimerValue | null>(null);

/** Implementação inerte: mantém a tela viva quando o provider não está montado. */
const INERT_TIMER: TaskTimerValue = {
  runningTaskId: null,
  runningProjectId: null,
  runningProjectName: null,
  runningTaskName: null,
  miniPlayerVisible: false,
  setMiniPlayerVisible: () => undefined,
  toggleMiniPlayerVisible: () => undefined,
  secondsFor: () => 0,
  toggle: async () => undefined,
  busy: false,
};

/** Evita repetir o aviso a cada render das linhas do board. */
const warnedOnce = new Set<string>();

function warnOnce(key: string, message: string): void {
  if (warnedOnce.has(key)) return;
  warnedOnce.add(key);
  console.warn(message);
}

function sessionAnchorId(session: TimerSession | null): string | null {
  return session?.taskId ?? session?.workItemId ?? null;
}

export function TaskTimerProvider({ children }: { children: ReactNode }) {
  const { show } = useToast();
  const [session, setSession] = useState<TimerSession | null>(null);
  const [sessionLabel, setSessionLabel] = useState<{
    projectName: string | null;
    taskName: string | null;
  }>({ projectName: null, taskName: null });
  const [miniPlayerVisible, setMiniPlayerVisible] = useState(false);
  const [serverStartedAt, setServerStartedAt] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [optimisticStart, setOptimisticStart] = useState<number | null>(null);
  const [now, setNow] = useState(() => Date.now());

  // Reidrata a sessão em andamento — o timer sobrevive a refresh e troca de página.
  useEffect(() => {
    let active = true;
    api.timesheet
      .currentTimer()
      .then((current) => {
        if (!active) return;
        setSession(current);
        if (current?.startedAt) {
          setServerStartedAt(new Date(current.startedAt).getTime());
        }
        setNow(Date.now());
      })
      .catch((err: unknown) => {
        console.warn('[TaskTimer] GET /timer/current falhou.', err);
      });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, []);

  const runningTaskId = sessionAnchorId(session);
  const runningProjectId = session?.projectId ?? null;

  useEffect(() => {
    if (!runningProjectId || !runningTaskId) return;

    let active = true;
    const projectId = runningProjectId;
    const taskId = runningTaskId;

    async function loadSessionLabel() {
      try {
        const [project, task] = await Promise.all([
          api.projects.getById(projectId),
          api.tasks.getById(projectId, taskId),
        ]);
        if (!active) return;
        setSessionLabel({
          projectName: project.name,
          taskName: task.name,
        });
      } catch (err: unknown) {
        console.warn('[TaskTimer] Não foi possível carregar nomes do timer atual.', err);
      }
    }

    void loadSessionLabel();

    return () => {
      active = false;
    };
  }, [runningProjectId, runningTaskId]);

  function secondsFor(taskId: string): number {
    // Prioridade 1: contador otimista (inicia imediatamente ao clicar)
    if (optimisticStart !== null) {
      return Math.max(0, Math.floor((now - optimisticStart) / 1000));
    }
    // Prioridade 2: usar startedAt do servidor (apenas se now >= startedAt)
    if (runningTaskId === taskId && serverStartedAt !== null) {
      if (now >= serverStartedAt) {
        return Math.max(0, Math.floor((now - serverStartedAt) / 1000));
      }
      // Se o servidor ainda não chegou, usa o optimisticStart mais recente
      // (isso acontece quando o startedAt é no futuro devido ao delay de rede)
    }
    // Prioridade 3: fallback com session.startedAt
    if (runningTaskId === taskId && session) {
      const startedAt = new Date(session.startedAt).getTime();
      if (!Number.isNaN(startedAt) && now >= startedAt) {
        return Math.max(0, Math.floor((now - startedAt) / 1000));
      }
    }
    return 0;
  }

  async function stopSession(current: TimerSession): Promise<void> {
    await api.timesheet.stopTimer(current.id);
    setSession(null);
    setSessionLabel({ projectName: null, taskName: null });
    setServerStartedAt(null);
    setOptimisticStart(null);
  }

  async function toggle(
    taskId: string,
    projectId: string,
    metadata?: { projectName?: string | null; taskName?: string | null },
  ): Promise<void> {
    if (busy) return;
    setBusy(true);
    setOptimisticStart(Date.now());
    try {
      if (sessionAnchorId(session) === taskId) {
        if (session) await stopSession(session);
        setOptimisticStart(null);
        show({ title: 'Controle de tempo encerrado.', tone: 'success' });
        return;
      }
      if (session) await stopSession(session);
      const newSession = await api.timesheet.startTimer(projectId, taskId);
      setSession(newSession);
      setSessionLabel({
        projectName: metadata?.projectName ?? null,
        taskName: metadata?.taskName ?? null,
      });
      // Usa o startedAt da resposta do servidor
      if (newSession.startedAt) {
        setServerStartedAt(new Date(newSession.startedAt).getTime());
      }
      // Não limpa optimisticStart aqui - será limpo automaticamente quando now >= serverStartedAt
      setNow(Date.now());
      show({ title: 'Controle de tempo iniciado.', tone: 'success' });
    } catch (err: unknown) {
      setOptimisticStart(null);
      const current = await api.timesheet.currentTimer().catch(() => null);
      setSession(current);
      setSessionLabel({ projectName: null, taskName: null });
      if (current?.startedAt) {
        setServerStartedAt(new Date(current.startedAt).getTime());
      } else {
        setServerStartedAt(null);
      }
      setNow(Date.now());
      show({
        ...formatApiError(err, 'update'),
        tone: 'error',
      });
    } finally {
      setBusy(false);
    }
  }

  return (
    <TaskTimerContext.Provider
      value={{
        runningTaskId,
        runningProjectId,
        runningProjectName: sessionLabel.projectName,
        runningTaskName: sessionLabel.taskName,
        miniPlayerVisible,
        setMiniPlayerVisible,
        toggleMiniPlayerVisible: () => setMiniPlayerVisible((visible) => !visible),
        secondsFor,
        toggle,
        busy,
      }}
    >
      {children}
    </TaskTimerContext.Provider>
  );
}

export function useTaskTimer(): TaskTimerValue {
  const value = useContext(TaskTimerContext);
  if (!value) {
    warnOnce(
      'missing-provider',
      '[TaskTimer] useTaskTimer fora do TaskTimerProvider — recarregue a página.',
    );
  }
  return value ?? INERT_TIMER;
}
